import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Button, Tag } from 'antd';
import { EditOutlined, UndoOutlined, DeleteOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import moment from 'moment';

import { setCustomEmails } from '../redux/actions/customEmailsActions';
import { t } from '../i18n/i18n';
import useTranslations from './useTranslations';
import {
  getCustomEmailsAll,
  getTemplateByDefault,
  getTemplateById,
} from '../API/services/alkymersService';

export const useCustomEmails = () => {
  const dispatch = useDispatch();
  const { key } = useTranslations();
  const [sendMail, setSendMail] = useState('');
  const [editMailVisible, setEditMailVisible] = useState(null);
  const [restartTemplate, setRestartTemplate] = useState(null);
  const [deleteTemplate, setDeleteTemplate] = useState(null);
  const [customEmailDefault, setCustomEmailDefault] = useState();

  moment.updateLocale(key);
  const customEmails = useSelector((state) => state.customEmails.customEmailsPaginated);

  const getDefaultTemplate = async () => {
    const id = 'd-c9096b03a2c14af6b22551197e6130cf';
    const template = await getTemplateById(id);
    return template?.versions[0]?.html_content;
  };

  const translateEmail = (data, LABEL) => {
    try {
      return data?.translations[key][LABEL] || t(LABEL);
    } catch (error) {
      return t(LABEL);
    }
  };

  const TooltipCustom = ({ title = '', text = '', type = 'default' }) => (
    <OverlayTrigger placement="left" overlay={<Tooltip placement="left">{title}</Tooltip>}>
      <Tag
        color={
          type === 'default'
            ? 'blue'
            : type === 'custom'
            ? 'green'
            : type === 'manual'
            ? 'orange'
            : 'cyan'
        }
        className="email-type-badge"
        style={{ minWidth: '90px', textAlign: 'center', display: 'inline-block' }}
      >
        {text}
      </Tag>
    </OverlayTrigger>
  );

  const mappingDataToRowTable = async (data) => {
    const defaultTemplate = await getDefaultTemplate();

    return data?.map((item, indexRow) => {
      const subjectTranslated = translateEmail(item, item?.subject);
      const isCustom = item?.companyId || item?.workspaceId;
      const wasCreatedByPartner = item?.eventType === 'DEFAULT_EMAIL';
      const newItem = {
        ...item,
        title: (
          <span className="text-ellipsis" title={translateEmail(item, item?.title)}>
            {translateEmail(item, item?.title)}
          </span>
        ),
        subjectTranslated: (
          <span className="text-ellipsis" title={subjectTranslated}>
            {subjectTranslated}
          </span>
        ),
        typeColumn: !isCustom ? (
          <TooltipCustom text={t('DEFAULT_EMAIL')} title={t('DEFAULT_ALKEMY')} type="default" />
        ) : (
          <TooltipCustom text={t('CUSTOM_EMAIL')} title={t('CUSTOM_ALKEMY')} type="custom" />
        ),
        sendTypeColumn:
          item?.sendType === 'MANUAL' ? (
            <TooltipCustom text={t('MANUAL')} title={t('MANUAL_EMAIL')} type="manual" />
          ) : (
            <TooltipCustom text={t('AUTOMATIC')} title={t('AUTOMATIC_EMAIL')} type="automatic" />
          ),
      };
      return {
        ...newItem,

        actions: (
          <div className="action-buttons">
            <OverlayTrigger
              placement="left"
              overlay={<Tooltip placement="left">{t('EDIT')}</Tooltip>}
            >
              <Button
                type="text"
                icon={<EditOutlined />}
                onClick={() => setEditMailVisible(item)}
              />
            </OverlayTrigger>
            {isCustom && !wasCreatedByPartner && (
              <OverlayTrigger
                placement="left"
                overlay={<Tooltip placement="left">{t('RESTART_LABEL')}</Tooltip>}
              >
                <Button
                  type="text"
                  icon={<UndoOutlined />}
                  onClick={() => setRestartTemplate(item)}
                />
              </OverlayTrigger>
            )}
            {wasCreatedByPartner && (
              <OverlayTrigger
                placement="left"
                overlay={<Tooltip placement="left">{t('DELETE_LABEL')}</Tooltip>}
              >
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  className="danger-action"
                  onClick={() => setDeleteTemplate(item)}
                />
              </OverlayTrigger>
            )}
          </div>
        ),
      };
    });
  };

  const getCustomEmails = async (params) => {
    const { limit, offset, ...rest } = params;
    const response = await getCustomEmailsAll(params);
    const list = response?.data;
    const result = {
      list,
      filter: rest,
      pagination: { limit, offset },
    };
    dispatch(setCustomEmails(result));
    return result;
  };

  const getCustomEmailsDefault = async () => {
    const response = await getTemplateByDefault();
    setCustomEmailDefault(response);
  };

  useEffect(() => {
    getCustomEmailsDefault();
  }, []);

  const getCustomEmailsForTable = async (filters) => {
    const { limit, offset, ...rest } = filters;

    dispatch(setCustomEmails({ ...customEmails, isLoading: true, reload: offset === 0 }));

    try {
      if (offset > 0 && Object.keys(rest).length > 1 && customEmails?.pagination?.endPagination) {
        dispatch(setCustomEmails({ ...customEmails, isLoading: false, reload: false }));
        return [];
      }

      const responseData = await getCustomEmailsAll({
        ...filters,
      });
      const collection = responseData?.collection;
      const pagination = responseData?.pagination;
      const tabsFilter = responseData?.extraData?.tabsFilter;

      const listWitoutRepeat = collection;

      const total =
        !offset && !filters?.customFilters?.length
          ? pagination?.total
          : customEmails?.pagination?.total;

      const totalTabs = !offset
        ? filters?.customFilters?.length
          ? customEmails?.tabsFilter
          : tabsFilter
        : customEmails?.tabsFilter;

      const listData = await mappingDataToRowTable(listWitoutRepeat);
      const result = {
        list: offset ? [...customEmails?.list, ...listData] : listData,
        filter: rest,
        pagination: {
          total,
          limit,
          offset,
          endPagination: !listWitoutRepeat?.length,
        },
        tabsFilter: totalTabs,
      };

      dispatch(setCustomEmails({ ...result, isLoading: false, reload: false }));
      return result;
    } catch (error) {
      dispatch(setCustomEmails({ isLoading: false, reload: false }));
    }
    return customEmails;
  };

  return {
    getCustomEmails,
    getCustomEmailsForTable,
    customEmailDefault,
    sendMail,
    setSendMail,
    editMailVisible,
    setEditMailVisible,
    restartTemplate,
    setRestartTemplate,
    deleteTemplate,
    setDeleteTemplate,
    translateEmail,
  };
};
